import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg } from "@/lib/api";

export const NONE = "__none";

function initValues(fields, initial) {
  const v = {};
  fields.forEach((f) => {
    const raw = initial?.[f.name];
    if (f.type === "checkbox") v[f.name] = raw ?? f.default ?? false;
    else if (f.type === "select") v[f.name] = raw ?? f.default ?? (f.nullable ? NONE : "");
    else v[f.name] = raw ?? f.default ?? "";
  });
  return v;
}

function Field({ f, value, set, values, testid }) {
  const id = `${testid}-${f.name}`;
  if (f.type === "checkbox") {
    return (
      <div className="flex items-center gap-3 pt-2">
        <Switch id={id} data-testid={id} checked={!!value} onCheckedChange={(c) => set(f.name, c)} />
        <Label htmlFor={id} className="text-sm">{f.label}</Label>
      </div>
    );
  }
  let control;
  if (f.type === "select") {
    const opts = typeof f.options === "function" ? f.options(values) : f.options;
    control = (
      <Select value={value || undefined} onValueChange={(x) => set(f.name, x)}>
        <SelectTrigger data-testid={id}><SelectValue placeholder={f.placeholder || "Select…"} /></SelectTrigger>
        <SelectContent>
          {f.nullable && <SelectItem value={NONE}>{f.noneLabel || "None"}</SelectItem>}
          {opts.map((o) => <SelectItem key={o.value} value={String(o.value)} data-testid={`${id}-opt-${o.value}`}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  } else if (f.type === "textarea") {
    control = <Textarea id={id} data-testid={id} rows={f.rows || 3} value={value} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} />;
  } else {
    control = (
      <Input id={id} data-testid={id} type={f.type || "text"} step={f.type === "number" ? f.step || "0.01" : undefined}
        required={f.required} value={value} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} />
    );
  }
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm text-slate-700">{f.label}{f.required && <span className="text-rose-600"> *</span>}</Label>
      {control}
      {f.help && <p className="text-xs text-slate-500">{f.help}</p>}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, description, fields, initial, onSubmit, submitLabel = "Save", testid = "form" }) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setValues(initValues(fields, initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && f.type === "select" && (!values[f.name] || values[f.name] === NONE)) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const out = { ...values };
    fields.forEach((f) => {
      if (f.type === "number") out[f.name] = out[f.name] === "" ? (f.nullable ? null : 0) : Number(out[f.name]);
      if (out[f.name] === NONE || (f.nullable && out[f.name] === "")) out[f.name] = null;
    });
    setBusy(true);
    try {
      await onSubmit(out);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const visible = fields.filter((f) => !f.show || f.show(values));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {visible.map((f) => (
            <div key={f.name} className={f.full || f.type === "textarea" ? "sm:col-span-2" : ""}>
              <Field f={f} value={values[f.name] ?? ""} set={set} values={values} testid={testid} />
            </div>
          ))}
          <DialogFooter className="sm:col-span-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`${testid}-submit`}>
              {busy ? "Saving…" : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
